/**
 * DocumentsPage — Landlord document management hub.
 *
 * Architecture:
 *  - Documents stored in Supabase Storage ('documents' bucket, public).
 *  - Metadata in the `documents` table (owner_id, property_id, tenant_link_id, status, signing_token).
 *  - Signing flow: landlord generates a UUID token → URL /sign/:token → tenant signs.
 *  - Status machine: draft → sent → signed.
 */

import { useEffect, useState } from 'react';
import {
    FileText, Upload, Send, Eye, X, Loader2,
    Check, Clock, Copy, ChevronRight, Zap, CheckCircle2, Lock,
} from 'lucide-react';
import { createNotification } from '../utils/notifications';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/useAppContext';
import { supabase } from '../lib/supabase';
import { formatDate } from '../utils/formatters';
import { getPlanLimits, formatLimit, hasFeature } from '../lib/plans';
import EmptyState from '../components/EmptyState';

// ─── Types ────────────────────────────────────────────────────────────────────

interface RentflowDocument {
    id: string;
    owner_id: string;
    property_id: string | null;
    tenant_link_id: string | null;
    name: string;
    type: string;
    file_path: string;
    file_url: string | null;
    status: 'draft' | 'sent' | 'signed';
    signing_token: string | null;
    signed_at: string | null;
    created_at: string;
    updated_at: string;
    // Joined relations
    property?: { name: string } | null;
    tenant_link?: { tenant_name: string; tenant_email: string } | null;
}

const DOC_TYPES = [
    { value: 'lease_agreement', label: 'Lease Agreement' },
    { value: 'handover_protocol', label: 'Handover Protocol' },
    { value: 'notice', label: 'Notice' },
    { value: 'custom', label: 'Custom Document' },
];

// ─── Shared style constants ───────────────────────────────────────────────────

const labelStyle: React.CSSProperties = {
    fontSize: '0.8125rem',
    fontWeight: 800,
    marginBottom: '8px',
    display: 'block',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
};

const inputStyle: React.CSSProperties = {
    borderRadius: '12px',
    padding: '1rem 1.25rem',
    border: '1px solid #e2e8f0',
    fontSize: '1rem',
    width: '100%',
    background: '#f8fafc',
    fontWeight: 600,
    color: '#1a1d2e',
    outline: 'none',
    boxSizing: 'border-box',
};

const selectStyle: React.CSSProperties = {
    width: '100%',
    padding: '1rem 1.25rem',
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
    fontSize: '1rem',
    background: '#f8fafc',
    fontWeight: 600,
    cursor: 'pointer',
    appearance: 'none' as const,
    boxSizing: 'border-box' as const,
};

// ─── StatusChip ───────────────────────────────────────────────────────────────

function StatusChip({ status }: { status: string }) {
    const config: Record<string, { label: string; color: string; bg: string }> = {
        draft: { label: 'Draft', color: '#64748b', bg: 'rgba(100,116,139,0.1)' },
        sent: { label: 'Awaiting Signature', color: '#d97706', bg: 'rgba(245,158,11,0.1)' },
        signed: { label: 'Signed', color: '#16a34a', bg: 'rgba(34,197,94,0.1)' },
    };
    const cfg = config[status] ?? config.draft;

    return (
        <span style={{
            padding: '4px 10px', borderRadius: '8px', fontSize: '0.75rem', fontWeight: 700,
            color: cfg.color, background: cfg.bg,
            display: 'inline-flex', alignItems: 'center', gap: '5px',
        }}>
            {status === 'signed' && <Check size={11} />}
            {status === 'sent' && <Clock size={11} />}
            {cfg.label}
        </span>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function DocumentsPage() {
    const { user } = useAppContext();
    const navigate = useNavigate();

    // Data
    const [documents, setDocuments] = useState<RentflowDocument[]>([]);
    const [showUpgradeGate, setShowUpgradeGate] = useState(false);
    const [properties, setProperties] = useState<{ id: string; name: string }[]>([]);
    const [tenantLinks, setTenantLinks] = useState<{ id: string; tenant_name: string; tenant_email: string; property_id: string; tenant_user_id: string | null }[]>([]);
    const [loading, setLoading] = useState(true);

    // UI state
    const [isUploadOpen, setIsUploadOpen] = useState(false);
    const [selectedDoc, setSelectedDoc] = useState<RentflowDocument | null>(null);
    const [selectedDocSignature, setSelectedDocSignature] = useState<{ signer_name: string; signature_path: string; signed_at: string } | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [sendingId, setSendingId] = useState<string | null>(null);
    const [copiedToken, setCopiedToken] = useState<string | null>(null);
    const [showEsignGate, setShowEsignGate] = useState(false);

    const canESign = hasFeature(user.plan, 'esigning');

    // Upload form
    const [docName, setDocName] = useState('');
    const [docType, setDocType] = useState('lease_agreement');
    const [docPropertyId, setDocPropertyId] = useState('');
    const [docTenantLinkId, setDocTenantLinkId] = useState('');
    const [docFile, setDocFile] = useState<File | null>(null);
    const [sendAfterUpload, setSendAfterUpload] = useState(false);

    // ─── Data fetching ─────────────────────────────────────────────────────

    const fetchDocuments = async () => {
        if (!user.id) return;
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('documents')
                .select(`
                    *,
                    property:properties(name),
                    tenant_link:tenant_links(tenant_name, tenant_email)
                `)
                .eq('owner_id', user.id)
                .order('created_at', { ascending: false });

            if (error) throw error;
            setDocuments((data as RentflowDocument[]) || []);
        } catch (err) {
            console.error('[Documents] fetch error:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchSupportData = async () => {
        if (!user.id) return;

        const { data: propsData } = await supabase
            .from('properties')
            .select('id, name')
            .eq('owner_id', user.id);

        const propIds = (propsData || []).map((p) => p.id);
        setProperties(propsData || []);

        if (propIds.length === 0) return;

        const { data: linksData } = await supabase
            .from('tenant_links')
            .select('id, tenant_name, tenant_email, property_id, tenant_user_id')
            .in('property_id', propIds);

        setTenantLinks(linksData || []);
    };

    useEffect(() => {
        fetchDocuments();
        fetchSupportData();

        // Real-time: re-fetch when any document owned by this landlord changes
        // (e.g. tenant signs → status flips to 'signed' → landlord sees it immediately)
        if (!user.id) return;
        const channel = supabase
            .channel('documents-landlord-' + user.id)
            .on(
                'postgres_changes',
                { event: '*', schema: 'public', table: 'documents', filter: `owner_id=eq.${user.id}` },
                () => { fetchDocuments(); }
            )
            .subscribe();

        return () => { supabase.removeChannel(channel); };
    }, [user.id]);

    // ─── Fetch signature when opening a signed document ─────────────────────
    useEffect(() => {
        if (!selectedDoc || selectedDoc.status !== 'signed') {
            setSelectedDocSignature(null);
            return;
        }
        supabase
            .from('document_signatures')
            .select('signer_name, signature_path, signed_at')
            .eq('document_id', selectedDoc.id)
            .order('signed_at', { ascending: false })
            .limit(1)
            .maybeSingle()
            .then(({ data }) => setSelectedDocSignature(data));
    }, [selectedDoc?.id, selectedDoc?.status]);

    // ─── Tenant links filtered by selected property ─────────────────────────
    const filteredTenantLinks = docPropertyId
        ? tenantLinks.filter((t) => t.property_id === docPropertyId)
        : tenantLinks;

    // ─── Handlers ──────────────────────────────────────────────────────────

    const resetForm = () => {
        setDocName('');
        setDocType('lease_agreement');
        setDocPropertyId('');
        setDocTenantLinkId('');
        setDocFile(null);
        setSendAfterUpload(false);
    };

    const docLimit = getPlanLimits(user.plan).documents;

    const handleUploadClick = () => {
        if (documents.length >= docLimit) {
            setShowUpgradeGate(true);
            return;
        }
        setIsUploadOpen(true);
    };

    const handleUpload = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!docFile || !docName || !user.id) return;

        // Re-check limit (race condition guard)
        if (documents.length >= docLimit) {
            setIsUploadOpen(false);
            setShowUpgradeGate(true);
            return;
        }

        // Block "send immediately" if plan doesn't include e-signing
        if (sendAfterUpload && !canESign) {
            setIsUploadOpen(false);
            setShowEsignGate(true);
            return;
        }

        setIsSubmitting(true);
        try {
            // 1. Upload file to Supabase Storage
            const fileExt = docFile.name.split('.').pop() || 'pdf';
            const safeName = docName.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_\-]/g, '');
            const fileName = `${user.id}/${Date.now()}_${safeName}.${fileExt}`;

            const { error: uploadError } = await supabase.storage
                .from('documents')
                .upload(fileName, docFile, { cacheControl: '3600', upsert: false });

            if (uploadError) throw uploadError;

            // 2. Get public URL
            const { data: urlData } = supabase.storage.from('documents').getPublicUrl(fileName);

            // 3. Optionally generate a signing token
            const signingToken = sendAfterUpload ? crypto.randomUUID() : null;

            // 4. Insert document metadata
            const { error: insertError } = await supabase.from('documents').insert({
                owner_id: user.id,
                property_id: docPropertyId || null,
                tenant_link_id: docTenantLinkId || null,
                name: docName,
                type: docType,
                file_path: fileName,
                file_url: urlData.publicUrl,
                status: sendAfterUpload ? 'sent' : 'draft',
                signing_token: signingToken,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
            });

            if (insertError) throw insertError;

            // Notify tenant if document is sent immediately
            if (sendAfterUpload && docTenantLinkId) {
                const link = tenantLinks.find(t => t.id === docTenantLinkId);
                if (link?.tenant_user_id) {
                    await createNotification(
                        link.tenant_user_id,
                        'document_sent',
                        'Document awaiting signature',
                        docName,
                        signingToken ? `/sign/${signingToken}` : '/app/tenant/documents'
                    );
                }
            }

            setIsUploadOpen(false);
            resetForm();
            fetchDocuments();
        } catch (err: unknown) {
            const message = (err as any)?.message || (err instanceof Error ? err.message : 'Unknown error');
            console.error('[Documents] upload error:', err);
            alert('Upload failed: ' + message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSendForSignature = async (doc: RentflowDocument) => {
        if (!canESign) {
            setShowEsignGate(true);
            return;
        }
        setSendingId(doc.id);
        try {
            const token = crypto.randomUUID();
            const { error } = await supabase
                .from('documents')
                .update({ status: 'sent', signing_token: token, updated_at: new Date().toISOString() })
                .eq('id', doc.id);

            if (error) throw error;

            // Notify tenant if linked
            if (doc.tenant_link_id) {
                const link = tenantLinks.find(t => t.id === doc.tenant_link_id);
                if (link?.tenant_user_id) {
                    await createNotification(
                        link.tenant_user_id,
                        'document_sent',
                        'Document awaiting signature',
                        doc.name,
                        `/sign/${token}`
                    );
                }
            }

            fetchDocuments();
        } catch (err: unknown) {
            const message = (err as any)?.message || (err instanceof Error ? err.message : 'Unknown error');
            alert('Error: ' + message);
        } finally {
            setSendingId(null);
        }
    };

    const copySignLink = (token: string) => {
        const url = `${window.location.origin}/sign/${token}`;
        navigator.clipboard.writeText(url).catch(() => {
            // Fallback for browsers that block clipboard
            prompt('Copy signing link:', url);
        });
        setCopiedToken(token);
        setTimeout(() => setCopiedToken(null), 2500);
    };

    const docTypeLabel = (type: string) =>
        DOC_TYPES.find((t) => t.value === type)?.label ?? type;

    // ─── Render ─────────────────────────────────────────────────────────────

    return (
        <div className="page dashboard-page">
            {/* ── Page header ── */}
            <div className="page__header dashboard-page-header">
                <div className="dashboard-page-heading">
                    <h1
                        className="page__title"
                        style={{ fontSize: '2.5rem', fontWeight: 900, letterSpacing: '-0.04em', marginBottom: '8px', color: '#1a1d2e' }}
                    >
                        Documents
                    </h1>
                    <p
                        className="page__subtitle"
                        style={{ fontSize: '1.125rem', fontWeight: 500, color: 'var(--color-text-secondary)' }}
                    >
                        {documents.length} document{documents.length !== 1 ? 's' : ''} — leases, protocols &amp; notices
                    </p>
                </div>
                <div className="dashboard-page-actions" style={{ alignItems: 'center', gap: '12px' }}>
                    {!loading && (
                        <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: documents.length >= docLimit ? '#ef4444' : '#64748b', background: documents.length >= docLimit ? 'rgba(239,68,68,0.08)' : '#f1f5f9', padding: '6px 12px', borderRadius: '10px' }}>
                            {documents.length} / {formatLimit(docLimit)} docs
                        </span>
                    )}
                    <button
                        className="btn btn-primary"
                        onClick={handleUploadClick}
                        disabled={loading}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '10px',
                            borderRadius: '14px', padding: '0.875rem 1.75rem',
                            fontWeight: 700,
                            background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                            border: 'none', boxShadow: '0 10px 15px -3px rgba(22,163,74,0.25)',
                        }}
                    >
                        <Upload size={18} /> Upload Document
                    </button>
                </div>
            </div>

            {/* ── Content ── */}
            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '10rem 0' }}>
                    <Loader2 className="animate-spin" size={48} color="var(--color-primary)" />
                </div>
            ) : documents.length === 0 ? (
                <EmptyState
                    icon={<FileText />}
                    title="No documents yet"
                    description="Upload your first document — a lease agreement, handover protocol, or a custom file. You can send it for e-signature straight away."
                    actionLabel="Upload Document"
                    onAction={handleUploadClick}
                />
            ) : (
                <div
                    className="data-panel"
                    style={{
                        border: 'none',
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02), 0 10px 15px -3px rgba(0,0,0,0.03)',
                        borderRadius: '24px',
                        background: '#fff',
                    }}
                >
                    <div style={{ overflowX: 'auto' }}>
                        <table className="data-table" style={{ minWidth: '900px', borderCollapse: 'separate', borderSpacing: 0 }}>
                            <thead style={{ background: 'var(--color-bg-subtle)' }}>
                                <tr>
                                    {['Document', 'Property', 'Tenant', 'Type', 'Status', 'Created', ''].map((h, i) => (
                                        <th
                                            key={i}
                                            style={{
                                                padding: '1.25rem 1.5rem', fontSize: '0.8125rem', fontWeight: 700,
                                                textTransform: 'uppercase', letterSpacing: '0.05em',
                                                color: 'var(--color-text-tertiary)',
                                                borderBottom: '1px solid var(--color-border-subtle)', textAlign: 'left',
                                            }}
                                        >
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {documents.map((doc) => (
                                    <tr
                                        key={doc.id}
                                        style={{ cursor: 'pointer', transition: 'background 0.15s' }}
                                        onMouseEnter={(e) => (e.currentTarget.style.background = '#fafafa')}
                                        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                                    >
                                        {/* Document name */}
                                        <td style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--color-border-subtle)' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <div style={{
                                                    width: 36, height: 36, borderRadius: '10px',
                                                    background: 'rgba(22,163,74,0.08)',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                                }}>
                                                    <FileText size={16} color="#16a34a" />
                                                </div>
                                                <div>
                                                    <div style={{ fontWeight: 700, color: '#1a1d2e', fontSize: '0.9375rem' }}>{doc.name}</div>
                                                    {doc.signed_at && (
                                                        <div style={{ fontSize: '0.75rem', color: '#16a34a', fontWeight: 600 }}>
                                                            Signed {formatDate(doc.signed_at)}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </td>

                                        {/* Property */}
                                        <td style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--color-border-subtle)', color: 'var(--color-text-secondary)', fontWeight: 500 }}>
                                            {doc.property?.name ?? <span style={{ color: 'var(--color-text-tertiary)' }}>—</span>}
                                        </td>

                                        {/* Tenant */}
                                        <td style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--color-border-subtle)', color: 'var(--color-text-secondary)', fontWeight: 500 }}>
                                            {doc.tenant_link?.tenant_name ?? <span style={{ color: 'var(--color-text-tertiary)' }}>—</span>}
                                        </td>

                                        {/* Type */}
                                        <td style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--color-border-subtle)' }}>
                                            <span style={{
                                                padding: '4px 10px', borderRadius: '8px', fontSize: '0.75rem',
                                                fontWeight: 600, background: 'var(--color-bg-subtle)', color: 'var(--color-text-secondary)',
                                            }}>
                                                {docTypeLabel(doc.type)}
                                            </span>
                                        </td>

                                        {/* Status */}
                                        <td style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--color-border-subtle)' }}>
                                            <StatusChip status={doc.status} />
                                        </td>

                                        {/* Date */}
                                        <td style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--color-border-subtle)', color: 'var(--color-text-tertiary)', fontSize: '0.875rem' }}>
                                            {formatDate(doc.created_at)}
                                        </td>

                                        {/* Actions */}
                                        <td style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--color-border-subtle)' }}>
                                            <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
                                                {/* View file */}
                                                {doc.file_url && (
                                                    <a
                                                        href={doc.file_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn btn-secondary btn-sm"
                                                        title="Open document"
                                                        style={{ padding: '0.4rem 0.625rem', borderRadius: '8px', display: 'flex', alignItems: 'center' }}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <Eye size={14} />
                                                    </a>
                                                )}

                                                {/* Send for signature */}
                                                {doc.status === 'draft' && (
                                                    <button
                                                        className="btn btn-secondary btn-sm"
                                                        title={canESign ? 'Send for signature' : 'E-signing requires Pro — click to upgrade'}
                                                        onClick={(e) => { e.stopPropagation(); handleSendForSignature(doc); }}
                                                        disabled={sendingId === doc.id}
                                                        style={{
                                                            padding: '0.4rem 0.75rem', borderRadius: '8px',
                                                            display: 'flex', alignItems: 'center', gap: '4px',
                                                            fontSize: '0.75rem', fontWeight: 600,
                                                            opacity: canESign ? 1 : 0.72,
                                                        }}
                                                    >
                                                        {sendingId === doc.id
                                                            ? <Loader2 size={13} className="animate-spin" />
                                                            : canESign
                                                                ? <Send size={13} />
                                                                : <Lock size={13} />}
                                                        Send
                                                    </button>
                                                )}

                                                {/* Copy signing link */}
                                                {doc.status === 'sent' && doc.signing_token && (
                                                    <button
                                                        className="btn btn-secondary btn-sm"
                                                        title="Copy signing link"
                                                        onClick={(e) => { e.stopPropagation(); copySignLink(doc.signing_token!); }}
                                                        style={{
                                                            padding: '0.4rem 0.75rem', borderRadius: '8px', fontSize: '0.75rem', fontWeight: 600,
                                                            display: 'flex', alignItems: 'center', gap: '4px',
                                                            color: copiedToken === doc.signing_token ? '#16a34a' : undefined,
                                                        }}
                                                    >
                                                        {copiedToken === doc.signing_token
                                                            ? <><Check size={13} /> Copied!</>
                                                            : <><Copy size={13} /> Link</>}
                                                    </button>
                                                )}

                                                {/* Details */}
                                                <button
                                                    className="btn btn-secondary btn-sm"
                                                    onClick={(e) => { e.stopPropagation(); setSelectedDoc(doc); }}
                                                    style={{ padding: '0.4rem 0.625rem', borderRadius: '8px', display: 'flex', alignItems: 'center' }}
                                                    title="View details"
                                                >
                                                    <ChevronRight size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ══════════════════════════════════════════════════════════════
                Upload Modal
            ══════════════════════════════════════════════════════════════ */}
            {isUploadOpen && (
                <div
                    className="dropdown-overlay dashboard-modal-shell"
                    onClick={() => { setIsUploadOpen(false); resetForm(); }}
                    style={{ background: 'rgba(15,23,42,0.65)' }}
                >
                    <div
                        className="dashboard-modal"
                        style={{ background: '#fff', padding: '3rem', boxShadow: '0 30px 60px -12px rgba(0,0,0,0.35)', animation: 'slideUp 0.4s cubic-bezier(0.16,1,0.3,1)', maxWidth: 560, width: '100%' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2.5rem' }}>
                            <div>
                                <div style={{ display: 'inline-flex', padding: '10px', borderRadius: '14px', background: 'rgba(22,163,74,0.08)', color: '#16a34a', marginBottom: '1rem' }}>
                                    <Upload size={22} />
                                </div>
                                <h3 style={{ margin: 0, fontSize: '1.75rem', fontWeight: 900, letterSpacing: '-0.04em', color: '#1a1d2e' }}>
                                    Upload Document
                                </h3>
                                <p style={{ fontSize: '0.9375rem', color: 'var(--color-text-secondary)', marginTop: '6px', fontWeight: 500 }}>
                                    Add a document and optionally send it for signature
                                </p>
                            </div>
                            <button
                                onClick={() => { setIsUploadOpen(false); resetForm(); }}
                                style={{ background: '#f8fafc', border: '1px solid #e2e8f0', cursor: 'pointer', color: '#64748b', padding: '10px', borderRadius: '12px', display: 'flex' }}
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <form onSubmit={handleUpload} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                            {/* Name */}
                            <div>
                                <label style={labelStyle}>Document Name</label>
                                <input
                                    type="text"
                                    placeholder="e.g. Lease Agreement – Unit 3A"
                                    value={docName}
                                    onChange={(e) => setDocName(e.target.value)}
                                    required
                                    style={inputStyle}
                                />
                            </div>

                            {/* Type + Property */}
                            <div className="dashboard-form-grid">
                                <div>
                                    <label style={labelStyle}>Document Type</label>
                                    <select value={docType} onChange={(e) => setDocType(e.target.value)} style={selectStyle}>
                                        {DOC_TYPES.map((t) => (
                                            <option key={t.value} value={t.value}>{t.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label style={labelStyle}>Property</label>
                                    <select
                                        value={docPropertyId}
                                        onChange={(e) => { setDocPropertyId(e.target.value); setDocTenantLinkId(''); }}
                                        style={selectStyle}
                                    >
                                        <option value="">No property</option>
                                        {properties.map((p) => (
                                            <option key={p.id} value={p.id}>{p.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Tenant */}
                            <div>
                                <label style={labelStyle}>Tenant (optional)</label>
                                <select value={docTenantLinkId} onChange={(e) => setDocTenantLinkId(e.target.value)} style={selectStyle}>
                                    <option value="">No tenant linked</option>
                                    {filteredTenantLinks.map((t) => (
                                        <option key={t.id} value={t.id}>{t.tenant_name} — {t.tenant_email}</option>
                                    ))}
                                </select>
                            </div>

                            {/* File upload */}
                            <div>
                                <label style={labelStyle}>Document File</label>
                                <div
                                    style={{
                                        border: `2px dashed ${docFile ? '#16a34a' : '#e2e8f0'}`,
                                        borderRadius: '16px', padding: '2rem', textAlign: 'center',
                                        cursor: 'pointer', background: docFile ? 'rgba(22,163,74,0.04)' : '#f8fafc',
                                        transition: 'all 0.2s',
                                    }}
                                    onClick={() => document.getElementById('doc-file-input')?.click()}
                                >
                                    {docFile ? (
                                        <div style={{ color: '#16a34a', fontWeight: 600 }}>
                                            <FileText size={24} style={{ marginBottom: '0.5rem', display: 'block', margin: '0 auto 0.5rem' }} />
                                            <div>{docFile.name}</div>
                                            <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-tertiary)', marginTop: '4px' }}>
                                                {(docFile.size / 1024 / 1024).toFixed(2)} MB
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={{ color: 'var(--color-text-tertiary)' }}>
                                            <Upload size={24} style={{ margin: '0 auto 0.5rem', display: 'block' }} />
                                            <div style={{ fontWeight: 600 }}>Click to upload</div>
                                            <div style={{ fontSize: '0.8125rem', marginTop: '4px' }}>PDF, DOC, DOCX</div>
                                        </div>
                                    )}
                                    <input
                                        id="doc-file-input"
                                        type="file"
                                        accept=".pdf,.doc,.docx"
                                        hidden
                                        onChange={(e) => setDocFile(e.target.files?.[0] ?? null)}
                                    />
                                </div>
                            </div>

                            {/* Send for signature toggle */}
                            <label
                                onClick={(e) => {
                                    if (!canESign) {
                                        e.preventDefault();
                                        setIsUploadOpen(false);
                                        setShowEsignGate(true);
                                    }
                                }}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '12px',
                                    padding: '1rem 1.25rem',
                                    background: !canESign ? '#fafafa' : sendAfterUpload ? 'rgba(99,102,241,0.06)' : 'rgba(99,102,241,0.03)',
                                    borderRadius: '12px',
                                    border: `1px solid ${!canESign ? '#e2e8f0' : sendAfterUpload ? 'rgba(99,102,241,0.25)' : 'rgba(99,102,241,0.12)'}`,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    opacity: canESign ? 1 : 0.85,
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={sendAfterUpload}
                                    onChange={(e) => setSendAfterUpload(e.target.checked)}
                                    disabled={!canESign}
                                    style={{ width: 18, height: 18, accentColor: '#16a34a', flexShrink: 0 }}
                                />
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 700, color: '#1a1d2e', fontSize: '0.9375rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                                        Send for signature immediately
                                        {!canESign && (
                                            <span style={{
                                                fontSize: '0.625rem',
                                                fontWeight: 800,
                                                letterSpacing: '0.05em',
                                                color: '#7c3aed',
                                                background: 'rgba(124,58,237,0.1)',
                                                padding: '2px 8px',
                                                borderRadius: 99,
                                                textTransform: 'uppercase',
                                            }}>
                                                Pro
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                                        {canESign
                                            ? 'Generates a unique signing link after upload'
                                            : 'Upgrade to Pro to unlock e-signing'}
                                    </div>
                                </div>
                            </label>

                            {/* Actions */}
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '1rem', borderTop: '1px solid var(--color-border-subtle)' }}>
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => { setIsUploadOpen(false); resetForm(); }}
                                    style={{ borderRadius: '12px', padding: '0.875rem 1.5rem', fontWeight: 700 }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={isSubmitting || !docFile || !docName}
                                    style={{
                                        borderRadius: '12px', padding: '0.875rem 2rem', fontWeight: 800,
                                        background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                                        boxShadow: '0 10px 20px -5px rgba(22,163,74,0.30)',
                                        display: 'flex', alignItems: 'center', gap: '8px',
                                    }}
                                >
                                    {isSubmitting
                                        ? <><Loader2 size={16} className="animate-spin" /> Uploading…</>
                                        : 'Upload Document'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ══════════════════════════════════════════════════════════════
                Document Detail Modal
            ══════════════════════════════════════════════════════════════ */}
            {selectedDoc && (
                <div
                    className="dropdown-overlay dashboard-modal-shell"
                    onClick={() => setSelectedDoc(null)}
                    style={{ background: 'rgba(15,23,42,0.65)' }}
                >
                    <div
                        className="dashboard-modal"
                        style={{ background: '#fff', padding: '3rem', boxShadow: '0 30px 60px -12px rgba(0,0,0,0.35)', animation: 'slideUp 0.4s cubic-bezier(0.16,1,0.3,1)', maxWidth: 520, width: '100%' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                            <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 900, color: '#1a1d2e' }}>Document Details</h3>
                            <button
                                onClick={() => setSelectedDoc(null)}
                                style={{ background: '#f8fafc', border: '1px solid #e2e8f0', cursor: 'pointer', color: '#64748b', padding: '8px', borderRadius: '10px', display: 'flex' }}
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {/* Metadata rows */}
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            {([
                                ['Name', selectedDoc.name],
                                ['Type', docTypeLabel(selectedDoc.type)],
                                ['Property', selectedDoc.property?.name ?? '—'],
                                ['Tenant', selectedDoc.tenant_link?.tenant_name ?? '—'],
                                ['Created', formatDate(selectedDoc.created_at)],
                                ['Updated', formatDate(selectedDoc.updated_at)],
                                ...(selectedDoc.signed_at ? [['Signed', formatDate(selectedDoc.signed_at)]] : []),
                            ] as [string, string][]).map(([label, val]) => (
                                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.875rem 0', borderBottom: '1px solid var(--color-border-subtle)' }}>
                                    <span style={{ fontSize: '0.875rem', color: 'var(--color-text-tertiary)', fontWeight: 600 }}>{label}</span>
                                    <span style={{ fontSize: '0.9375rem', color: '#1a1d2e', fontWeight: 700 }}>{val}</span>
                                </div>
                            ))}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.875rem 0', borderBottom: '1px solid var(--color-border-subtle)' }}>
                                <span style={{ fontSize: '0.875rem', color: 'var(--color-text-tertiary)', fontWeight: 600 }}>Status</span>
                                <StatusChip status={selectedDoc.status} />
                            </div>
                        </div>

                        {/* Signature image — shown for signed documents */}
                        {selectedDoc.status === 'signed' && (
                            <div style={{ marginTop: '1.5rem', padding: '1.25rem', background: 'rgba(34,197,94,0.04)', borderRadius: '14px', border: '1px solid rgba(34,197,94,0.18)' }}>
                                <div style={{ fontSize: '0.75rem', fontWeight: 800, color: '#16a34a', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.75rem' }}>
                                    ✓ Signature on file
                                </div>
                                {selectedDocSignature ? (
                                    <>
                                        <div style={{ fontSize: '0.8125rem', color: '#64748b', marginBottom: '0.625rem' }}>
                                            Signed by <strong>{selectedDocSignature.signer_name}</strong> on {formatDate(selectedDocSignature.signed_at)}
                                        </div>
                                        <img
                                            src={selectedDocSignature.signature_path}
                                            alt="Tenant signature"
                                            style={{
                                                width: '100%', maxHeight: 120, objectFit: 'contain',
                                                background: '#fafafa', borderRadius: '10px',
                                                border: '1px solid #e2e8f0', padding: '0.5rem',
                                            }}
                                        />
                                    </>
                                ) : (
                                    <div style={{ fontSize: '0.875rem', color: '#64748b' }}>Loading signature…</div>
                                )}
                            </div>
                        )}

                        {/* Action buttons */}
                        <div style={{ display: 'flex', gap: '10px', marginTop: '1.5rem', flexWrap: 'wrap' }}>
                            {selectedDoc.file_url && (
                                <a
                                    href={selectedDoc.file_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-secondary"
                                    style={{ borderRadius: '10px', padding: '0.75rem 1.25rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px', textDecoration: 'none' }}
                                >
                                    <Eye size={15} /> View File
                                </a>
                            )}

                            {selectedDoc.status === 'draft' && (
                                <button
                                    className="btn btn-primary"
                                    onClick={() => {
                                        if (!canESign) {
                                            setSelectedDoc(null);
                                            setShowEsignGate(true);
                                            return;
                                        }
                                        handleSendForSignature(selectedDoc);
                                        setSelectedDoc(null);
                                    }}
                                    style={{
                                        borderRadius: '10px', padding: '0.75rem 1.25rem', fontWeight: 700,
                                        background: canESign
                                            ? 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)'
                                            : 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)',
                                        display: 'flex', alignItems: 'center', gap: '6px',
                                    }}
                                >
                                    {canESign ? <Send size={15} /> : <Lock size={15} />}
                                    {canESign ? 'Send for Signature' : 'Upgrade to Send'}
                                </button>
                            )}

                            {selectedDoc.status === 'sent' && selectedDoc.signing_token && (
                                <button
                                    className="btn btn-secondary"
                                    onClick={() => copySignLink(selectedDoc.signing_token!)}
                                    style={{ borderRadius: '10px', padding: '0.75rem 1.25rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px' }}
                                >
                                    {copiedToken === selectedDoc.signing_token
                                        ? <><Check size={15} /> Copied!</>
                                        : <><Copy size={15} /> Copy Signing Link</>}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Upgrade Gate Modal */}
            {showUpgradeGate && (
                <div
                    className="dropdown-overlay dashboard-modal-shell"
                    onClick={() => setShowUpgradeGate(false)}
                    style={{ background: 'rgba(15,23,42,0.65)', animation: 'fadeIn 0.2s ease-out', backdropFilter: 'blur(4px)' }}
                >
                    <div
                        style={{ background: '#fff', borderRadius: '28px', padding: '0', maxWidth: '460px', width: '90%', boxShadow: '0 30px 60px -12px rgba(0,0,0,0.35)', animation: 'slideUp 0.35s cubic-bezier(0.16,1,0.3,1)', overflow: 'hidden' }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div style={{ background: 'linear-gradient(135deg, #15803d 0%, #7c3aed 100%)', padding: '2.5rem 2.5rem 3.5rem', position: 'relative', overflow: 'hidden' }}>
                            <button onClick={() => setShowUpgradeGate(false)} style={{ position: 'absolute', top: '1.25rem', right: '1.25rem', background: 'rgba(255,255,255,0.15)', border: 'none', cursor: 'pointer', color: '#fff', padding: '6px', borderRadius: '50%', display: 'flex' }}>
                                <X size={16} />
                            </button>
                            <div style={{ width: '52px', height: '52px', background: 'rgba(255,255,255,0.15)', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.25rem' }}>
                                <Lock size={24} color="#fff" />
                            </div>
                            <h3 style={{ margin: '0 0 8px', fontSize: '1.625rem', fontWeight: 900, color: '#fff', letterSpacing: '-0.03em' }}>Unlock More Documents</h3>
                            <p style={{ margin: 0, fontSize: '0.9375rem', color: 'rgba(255,255,255,0.75)', fontWeight: 500, lineHeight: 1.5 }}>
                                You're on the Free plan — limited to {docLimit} documents. Upgrade to Pro for up to 100 documents and full e-signing.
                            </p>
                            <FileText size={120} style={{ position: 'absolute', bottom: '-20px', right: '-10px', opacity: 0.1, transform: 'rotate(-10deg)' }} />
                        </div>
                        <div style={{ padding: '2rem 2.5rem', borderBottom: '1px solid #f1f5f9' }}>
                            <p style={{ margin: '0 0 1rem', fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94a3b8' }}>What you get with Pro — €15/mo</p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                {['Up to 100 documents per account', 'Full e-signing workflow for all documents', 'Unlimited properties and unlimited tenants'].map(f => (
                                    <div key={f} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.9375rem', fontWeight: 600, color: '#1e293b' }}>
                                        <CheckCircle2 size={18} color="#16a34a" style={{ flexShrink: 0 }} /> {f}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div style={{ padding: '1.75rem 2.5rem', display: 'flex', gap: '12px' }}>
                            <button onClick={() => setShowUpgradeGate(false)} style={{ flex: 1, padding: '0.875rem', borderRadius: '14px', border: '1px solid #e2e8f0', background: '#f8fafc', fontWeight: 600, fontSize: '0.9375rem', color: '#64748b', cursor: 'pointer' }}>Maybe later</button>
                            <button onClick={() => { setShowUpgradeGate(false); navigate('/app/landlord/settings'); }} style={{ flex: 2, padding: '0.875rem', borderRadius: '14px', border: 'none', background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)', fontWeight: 800, fontSize: '0.9375rem', color: '#fff', cursor: 'pointer', boxShadow: '0 8px 20px -6px rgba(99,102,241,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                <Zap size={16} /> Upgrade to Pro
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* E-signing Gate Modal — Pro+ feature */}
            {showEsignGate && (
                <div
                    className="dropdown-overlay dashboard-modal-shell"
                    onClick={() => setShowEsignGate(false)}
                    style={{ background: 'rgba(15,23,42,0.65)', animation: 'fadeIn 0.2s ease-out', backdropFilter: 'blur(4px)' }}
                >
                    <div
                        style={{ background: '#fff', borderRadius: '28px', padding: '0', maxWidth: '460px', width: '90%', boxShadow: '0 30px 60px -12px rgba(0,0,0,0.35)', animation: 'slideUp 0.35s cubic-bezier(0.16,1,0.3,1)', overflow: 'hidden' }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)', padding: '2.5rem 2.5rem 3.5rem', position: 'relative', overflow: 'hidden' }}>
                            <button onClick={() => setShowEsignGate(false)} style={{ position: 'absolute', top: '1.25rem', right: '1.25rem', background: 'rgba(255,255,255,0.15)', border: 'none', cursor: 'pointer', color: '#fff', padding: '6px', borderRadius: '50%', display: 'flex' }}>
                                <X size={16} />
                            </button>
                            <div style={{ width: '52px', height: '52px', background: 'rgba(255,255,255,0.15)', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.25rem' }}>
                                <Send size={24} color="#fff" />
                            </div>
                            <div style={{ display: 'inline-block', fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.8)', background: 'rgba(255,255,255,0.15)', borderRadius: 99, padding: '4px 10px', marginBottom: 12 }}>
                                Pro plan required
                            </div>
                            <h3 style={{ margin: '0 0 8px', fontSize: '1.625rem', fontWeight: 900, color: '#fff', letterSpacing: '-0.03em' }}>
                                Document e-signing is a Pro feature
                            </h3>
                            <p style={{ margin: 0, fontSize: '0.9375rem', color: 'rgba(255,255,255,0.8)', fontWeight: 500, lineHeight: 1.5 }}>
                                You can still upload and store documents on your {user.plan} plan. Upgrade to Pro to send documents for legally-binding electronic signature.
                            </p>
                            <FileText size={120} style={{ position: 'absolute', bottom: '-20px', right: '-10px', opacity: 0.1, transform: 'rotate(-10deg)' }} />
                        </div>
                        <div style={{ padding: '2rem 2.5rem', borderBottom: '1px solid #f1f5f9' }}>
                            <p style={{ margin: '0 0 1rem', fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94a3b8' }}>What Pro unlocks</p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                {[
                                    'Send any document for electronic signature',
                                    'Automatic notifications to tenants',
                                    'Legally binding signature records',
                                    'Up to 100 documents and 20 properties',
                                ].map(f => (
                                    <div key={f} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.9375rem', fontWeight: 600, color: '#1e293b' }}>
                                        <CheckCircle2 size={18} color="#16a34a" style={{ flexShrink: 0 }} /> {f}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div style={{ padding: '1.75rem 2.5rem', display: 'flex', gap: '12px' }}>
                            <button onClick={() => setShowEsignGate(false)} style={{ flex: 1, padding: '0.875rem', borderRadius: '14px', border: '1px solid #e2e8f0', background: '#f8fafc', fontWeight: 600, fontSize: '0.9375rem', color: '#64748b', cursor: 'pointer' }}>Maybe later</button>
                            <button onClick={() => { setShowEsignGate(false); navigate('/app/landlord/settings'); }} style={{ flex: 2, padding: '0.875rem', borderRadius: '14px', border: 'none', background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)', fontWeight: 800, fontSize: '0.9375rem', color: '#fff', cursor: 'pointer', boxShadow: '0 8px 20px -6px rgba(22,163,74,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                <Zap size={16} /> Upgrade to Pro
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes slideUp {
                    from { transform: translateY(24px); opacity: 0; }
                    to   { transform: translateY(0);    opacity: 1; }
                }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
            `}</style>
        </div>
    );
}
